import React, {
  ReactNode,
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import useSWR from 'swr';

import { AuditLogEntryType, LogQueryArgs, pullAuditLogsForPipeline } from '../../services/logs_service';

import { getPollIntervalByStep, usePreferredTimeRange } from '../contexts/TimeRangeContext/TimeRangeContext';
import useClientContext from '../contexts/ClientData/ClientData';
import { usePipelineEdgeData } from '../VersionHistoryTimeline/PublishPipelineContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

interface PipelineLogsContextType {
  isLoading: boolean,
  hasError: boolean,
  error: string | null,
  logs: AuditLogEntryType[],
  nextPageToken: number | null,
  handleLoadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>,
}

interface Props {
  children: ReactNode | null,
  pipelineID: string,
  useAnomalyLogs: boolean | null,
  onLogsLoaded: () => void,
}

const PipelineLogsContext = React.createContext<PipelineLogsContextType>({
  logs: [],
  isLoading: false,
  error: null,
  hasError: false,
  nextPageToken: null,
  handleLoadNextPage: () => {},
})

export const usePipelineLogsContext = () => useContext(PipelineLogsContext);

export const PipelineLogsProvider = ({ 
  children,
  pipelineID,
  useAnomalyLogs = false,
  onLogsLoaded = () => {},
}: Props) => {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false);
  const [areLogsLoading, setLogsLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<AuditLogEntryType[]>([]);
  const [nextPageToken, setNextPageToken] = useState<number | null>(null);
  const [isMounted, setMounted] = useState<boolean>(true);

  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    startTimestamp: startDate,
    endTimestamp: endDate,
    step,
  }: any = usePreferredTimeRange()

  const { isEdgeEnabled, useArrowLogFormat } = useClientContext();
  const { selectedEdgeLocations } = usePipelineEdgeData();

  const locationNames = isEdgeEnabled && selectedEdgeLocations?.length ? selectedEdgeLocations.map(({ name }) => name) : null;

  const refreshInterval: number = getPollIntervalByStep(step)
  const queryKey = useAnomalyLogs ? `pipeline-anomaly-logs-${pipelineID}` : `pipeline-audit-logs-${pipelineID}`;
  const queryArgs: LogQueryArgs = [pipelineID, currentWorkspaceId, startDate, endDate, useArrowLogFormat, locationNames, null, useAnomalyLogs];
  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(
    () => areLogsLoading ? null : queryKey,
    () => pullAuditLogsForPipeline(queryArgs),
    {
      refreshInterval,
      focusThrottleInterval: refreshInterval,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      loadingTimeout: 1000,
      onLoadingSlow: () => setLogsLoading(true),
      onSuccess: () => setLogsLoading(false),
      onError: () => setLogsLoading(false),
      onDiscarded: () => setLogsLoading(false),
    }
  );

  useEffect(() => () => setMounted(false), []);

  useEffect(() => { mutate() }, [mutate, step]);

  useEffect(() => {
    if (error) {
      setLogs([]);
      setNextPageToken(null);
    } else if (data) {
      if (data?.logs?.length) {
        setLogs(data.logs);
      } else {
        setLogs([]);
      }
      onLogsLoaded();
      setNextPageToken(data.nextPageToken);
    }
  }, [data, error, onLogsLoaded])

  const handleLoadNextPage = useCallback(() => {
    if (nextPageToken === null || isLoadingNextPage || !isMounted || pipelineID === undefined) {
      return;
    }

    setIsLoadingNextPage(true);

    const queryParams: LogQueryArgs = [pipelineID, currentWorkspaceId, startDate, endDate, useArrowLogFormat, locationNames, nextPageToken, useAnomalyLogs];
    return pullAuditLogsForPipeline(queryParams)
      .then((response: any) => {
        setIsLoadingNextPage(false);
        setNextPageToken(response.nextPageToken);
        setLogs(log => log.concat(response.logs))
      })
  }, [currentWorkspaceId, endDate, isLoadingNextPage, isMounted, locationNames, nextPageToken, pipelineID, startDate, useArrowLogFormat, useAnomalyLogs]);
  
  const contextValue = useMemo(() => ({
    logs,
    error,
    isLoading,
    hasError: !!error,
    nextPageToken,
    handleLoadNextPage,
  }), [logs, error, isLoading, nextPageToken, handleLoadNextPage])

  return (
    <PipelineLogsContext.Provider value={contextValue}>
      {children}
    </PipelineLogsContext.Provider>
  )
}

export default usePipelineLogsContext;
